"use client";

import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { calculateProductPricing, getVariantLabel } from "../../utils";
import { useSelector } from "react-redux";
import { CURRENCY, DEFAULT_VAT_PERCENTAGE } from "../../constants/Variables";
import IOSSwitch from "../IsoSwitch";
import globalStyle from "../../globalStyle";

export default function PlanIncludedItemsTable({
  tableProps = {},
  tableHeader = [],
  tableData = [],
  displayRows = [],
  showPagination = false,
  loading = false,
  handleItemStatusChange,
  handleItemRemove,
  handleQuickOrder,
  handleUpdateItemQuantity,
}) {
  const currentLanguage = useSelector(
    (state) => state.languageReducer.currentLanguage,
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { t } = useTranslation(["common"]);
  const [expandedRowIds, setExpandedRowIds] = useState([]);
  const settings = useSelector((state) => state.settingsReducer.settings);
  const taxValue = settings?.tax || DEFAULT_VAT_PERCENTAGE;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const defaultExpandedIds = (tableData || [])
      .filter((row) => (row?.details?.products || []).length > 0)
      .map((row) => row?._id)
      .filter(Boolean);
    setExpandedRowIds(defaultExpandedIds);
  }, [tableData]);

  const handleToggleExpanded = (rowId) => {
    setExpandedRowIds((prev) => {
      if (prev.includes(rowId)) {
        return prev.filter((id) => id !== rowId);
      }
      return [...prev, rowId];
    });
  };

  const getIncludedProducts = (row) => row?.details?.productsList || [];

  return (
    <>
      <TableContainer sx={globalStyle.tableContainerStyle}>
        <Table {...tableProps}>
          <TableHead>
            <TableRow>
              {tableHeader?.map((header) => (
                <TableCell
                  key={header.id}
                  sx={{
                    textTransform: "capitalize",
                    ...header.style,
                  }}
                >
                  {t(header.label)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, rowIndex) => (
                <TableRow key={`plan-items-skeleton-${rowIndex}`}>
                  {(displayRows?.length
                    ? [...Array(displayRows.length)]
                    : [...Array(tableHeader?.length || 1)]
                  ).map((_, cellIndex) => (
                    <TableCell
                      key={`plan-items-skeleton-cell-${rowIndex}-${cellIndex}`}
                    >
                      <Skeleton
                        variant="rounded"
                        animation="wave"
                        height={26}
                        sx={{ borderRadius: "8px" }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : tableData?.length === 0 || !tableData ? (
              <TableRow>
                <TableCell
                  colSpan={displayRows?.length || tableHeader?.length || 1}
                  align="center"
                >
                  <Typography variant="h6" py={2}>
                    {t("noDataFound")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              (rowsPerPage > 0
                ? tableData?.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage,
                )
                : tableData
              )?.map((row, index) => {
                const planPricing = calculateProductPricing(
                  row?.details?.finalPrice,
                  0,
                  taxValue,
                );

                return (
                  <React.Fragment key={index}>
                    <TableRow>
                      {displayRows.map((val) => {
                        return (
                          <React.Fragment key={val}>
                            {val === "id_ascending" ? (
                              <TableCell>{index + 1}</TableCell>
                            ) : val === "item_type" ? (
                              <TableCell>
                                <Typography textTransform="capitalize">
                                  {row?.itemType?.replace("_", " ")}
                                </Typography>
                              </TableCell>
                            ) : val === "name_image" ? (
                              <TableCell>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={2}
                                >
                                  <Avatar
                                    src={row?.details?.coverImage}
                                    alt={row.details?.name[currentLanguage]}
                                    sx={{
                                      borderRadius: 2.5,
                                      // width: 100,
                                      // height: 100,
                                    }}
                                  />
                                  <Stack
                                    gap={0.2}
                                    direction="column"
                                    alignItems="flex-start"
                                  >
                                    <Typography variant="body1">
                                      {row?.details?.name[currentLanguage]}
                                    </Typography>

                                    {row?.itemType === "product" &&
                                      row?.details?.selectedVariant && (
                                        <Stack direction="row" alignItems="center" gap={0.2}>
                                          {row?.details?.isMegaDiscount && (
                                            <Box
                                              sx={{
                                                display: "inline-block",
                                                px: 1.5,
                                                py: 0.5,
                                                bgcolor: "#ff5722",
                                                color: "#fff",
                                                fontWeight: 700,
                                                fontSize: 11,
                                                borderRadius: "0 4px 4px 0",
                                                position: "relative",
                                                "&:before": {
                                                  content: '""',
                                                  position: "absolute",
                                                  left: -8,
                                                  top: 0,
                                                  borderTop: "12px solid transparent",
                                                  borderBottom: "12px solid transparent",
                                                  borderRight: "8px solid #ff5722",
                                                },
                                              }}
                                            >
                                              {t("megaDiscount")}
                                            </Box>
                                          )}
                                          <Chip
                                            sx={{ borderRadius: 1 }}
                                            label={
                                              getVariantLabel(
                                                row?.details?.selectedVariant
                                                  ?.attributes,
                                                currentLanguage,
                                              ) || "Selected variant"
                                            }
                                          />
                                        </Stack>
                                      )}

                                    {row?.itemType !== "product" &&
                                      getIncludedProducts(row)?.length > 0 && (
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          gap={0.2}
                                          onClick={() =>
                                            handleToggleExpanded(row?._id)
                                          }
                                          sx={{ cursor: "pointer" }}
                                        >
                                          <Typography
                                            variant="body2"
                                            color="primary.main"
                                            sx={{
                                              cursor: "pointer",
                                              textDecoration: "underline",
                                            }}
                                          >
                                            {t("viewIncludedItems")}
                                          </Typography>
                                          <ArrowRightIcon
                                            fontSize="small"
                                            sx={{
                                              transform:
                                                expandedRowIds.includes(
                                                  row?._id,
                                                )
                                                  ? "rotate(90deg)"
                                                  : "rotate(0deg)",
                                              transition: "transform 0.2s ease",
                                            }}
                                          />
                                        </Stack>
                                      )}
                                  </Stack>
                                </Stack>
                              </TableCell>
                            ) : val === "item_price" ? (
                              <TableCell>
                                <Typography>
                                  {planPricing.productPriceWithVat} {CURRENCY}
                                </Typography>
                              </TableCell>
                            ) : val === "item_availability" ? (
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={
                                    row?.details?.isOrderAllowed
                                      ? t("available")
                                      : t("notAvailable")
                                  }
                                  sx={{
                                    color: row?.details?.isOrderAllowed
                                      ? "success.main"
                                      : "error.main",
                                    // height: "28px",
                                    fontWeight: "600",
                                  }}
                                />
                              </TableCell>
                            ) : val === "item_quick_order" ? (
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={t(
                                    row?.itemType === "product"
                                      ? "addToCart"
                                      : "quickOrder",
                                  )}
                                  sx={{
                                    borderRadius: 1,
                                    background:
                                      row?.itemType === "product"
                                        ? "#7922D8"
                                        : "linear-gradient(90deg, #F1A24D 0%, #E56C5D 100%)",
                                    color: "white",
                                  }}
                                  clickable
                                  onClick={() => handleQuickOrder(row)}
                                />
                              </TableCell>
                            ) : val === "item_status" ? (
                              <TableCell>
                                <IOSSwitch
                                  checked={Boolean(row?.isActive)}

                                // onChange={() => handleItemStatusChange(row)}
                                />
                              </TableCell>
                            ) : val === "item_remove" ? (
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => handleItemRemove(row)}
                                >
                                  <DeleteIcon
                                    htmlColor="red"
                                    sx={{ fontSize: 18 }}
                                  />
                                </IconButton>
                              </TableCell>
                            ) : val === "item_qty" ? (
                              <TableCell>
                                {row?.selectedQty ?? 0}
                              </TableCell>
                            )

                              // : val === "item_qty" ? (
                              //   <TableCell>
                              //     {row?.itemType === "product" ? (
                              //       <Stack
                              //         direction="row"
                              //         alignItems="center"
                              //         spacing={0.5}
                              //       >
                              //         <IconButton
                              //           size="small"
                              //           disabled={row?.selectedQty <= 1}
                              //           onClick={() =>
                              //             handleUpdateItemQuantity(
                              //               row,
                              //               "decrease",
                              //             )
                              //           }
                              //           sx={{
                              //             width: 24,
                              //             height: 24,
                              //             border: "1px solid",
                              //             borderColor: "divider",
                              //             borderRadius: 1,
                              //           }}
                              //         >
                              //           <RemoveIcon sx={{ fontSize: 14 }} />
                              //         </IconButton>
                              //         <Typography
                              //           variant="body2"
                              //           sx={{
                              //             minWidth: 24,
                              //             textAlign: "center",
                              //             fontWeight: 600,
                              //           }}
                              //         >
                              //           {row?.selectedQty ?? 0}
                              //         </Typography>
                              //         <IconButton
                              //           size="small"
                              //           // disabled
                              //           onClick={() =>
                              //             handleUpdateItemQuantity(
                              //               row,
                              //               "increase",
                              //             )
                              //           }
                              //           sx={{
                              //             width: 24,
                              //             height: 24,
                              //             border: "1px solid",
                              //             borderColor: "divider",
                              //             borderRadius: 1,
                              //           }}
                              //         >
                              //           <AddIcon sx={{ fontSize: 14 }} />
                              //         </IconButton>
                              //       </Stack>
                              //     ) : (
                              //       <Typography variant="body2">
                              //         {row?.selectedQty ?? 0}
                              //       </Typography>
                              //     )}
                              //   </TableCell>
                              // )

                              : (
                                <TableCell>
                                  <Typography fontSize={13} lineHeight="normal">
                                    {row[val]}
                                  </Typography>
                                </TableCell>
                              )}
                          </React.Fragment>
                        );
                      })}
                    </TableRow>

                    {expandedRowIds.includes(row?._id) &&
                      getIncludedProducts(row)?.length > 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={
                              displayRows?.length || tableHeader?.length || 1
                            }
                            sx={{ py: 1.5 }}
                          >
                            <TableContainer
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                backgroundColor: "grey.50",
                              }}
                            >
                              <Table size="small">
                                <TableHead
                                  sx={{
                                    backgroundColor: "#b6b6b6 !important",
                                  }}
                                >
                                  <TableRow>
                                    <TableCell>
                                      {t("itemName")}
                                    </TableCell>
                                    <TableCell>
                                      {t("itemPrice")}
                                    </TableCell>
                                    <TableCell>
                                      {t("quantity")}
                                    </TableCell>
                                    <TableCell>
                                      {t("availabliity")}
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {getIncludedProducts(row).map(
                                    (item, includedIndex) => {
                                      const productPricing =
                                        calculateProductPricing(
                                          item?.finalPrice,
                                          0,
                                          taxValue,
                                        );
                                      return (
                                        <TableRow
                                          key={item?._id || includedIndex}
                                        >
                                          <TableCell>
                                            <Stack
                                              direction="row"
                                              alignItems="center"
                                              spacing={1}
                                            >
                                              <Avatar
                                                src={item?.coverImage}
                                                alt={
                                                  item?.name?.[currentLanguage]
                                                }
                                                sx={{
                                                  width: 38,
                                                  height: 38,
                                                  borderRadius: 1.5,
                                                }}
                                              />
                                              <Stack
                                                direction="column"
                                                alignItems="flex-start"
                                                gap={0.2}
                                              >
                                                <Typography variant="body2">
                                                  {item?.name?.[
                                                    currentLanguage
                                                  ] || "-"}
                                                </Typography>
                                                {item?.selectedVariant && (
                                                  <Chip
                                                    sx={{ borderRadius: 1 }}
                                                    label={
                                                      getVariantLabel(
                                                        item?.selectedVariant
                                                          ?.attributes,
                                                        currentLanguage,
                                                      ) || "Selected variant"
                                                    }
                                                  />
                                                )}
                                              </Stack>
                                            </Stack>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2">
                                              {`${productPricing.productPriceWithVat} ${CURRENCY}`}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2">
                                              {item?.selectedQty ?? 0}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              size="small"
                                              label={
                                                item?.isOrderAllowed
                                                  ? t("available")
                                                  : t("notAvailable")
                                              }
                                              sx={{
                                                color: item?.isOrderAllowed
                                                  ? "success.main"
                                                  : "error.main",
                                              }}
                                            />
                                          </TableCell>
                                        </TableRow>
                                      );
                                    },
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </TableCell>
                        </TableRow>
                      )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        sx={{ display: showPagination ? "block" : "none" }}
        rowsPerPageOptions={[10, 25, 50, 100, { label: "All", value: -1 }]}
        component="div"
        count={tableData?.length || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
}
